// askSmart-ui/src/pages/smrtcop/antennaDescriptors.ts
//
// Turns the RFDS "Antenna Position Details - Sector X (Final)" rows into the
// "MODEL/TECHNOLOGY" options the tape drop form offers, replacing the static
// list the template used to carry.
//
// The RFDS gives two rows per antenna column: `Model` (verbatim, e.g.
// "NNHH-65B-R4") and `Linked Cells` (e.g. "WTL01819_7A_2_F",
// "WTXN001819_N066A_1"). The cell names encode the band; this module decodes
// them into frequency tokens and joins everything with "/".
//
// ── Where the key comes from ────────────────────────────────────────────────
// Not invented here. The AT&T RET Worksheet ships three static reference
// tables to the right of the "RET Information" sheet (duplicated on "Example3"
// and "Sheet1"): band code → description → LTE band → NR band, and the Tech
// letter codes. The tables below are those, transcribed. AT&T's own Example3
// sheet also fixes the technology convention we follow: LTE-only cells → LTE,
// NR-only → 5G NR, mixed → "J (LTE & 5G NR)".
//
// Cross-checked against the RFDS itself, whose "Cell Details (Final)" table
// carries an explicit Frequency column and a USeId of the form
// {USID}.{sector}.{frequency}.{generation}.{carrier} (e.g. "80597.A.AWS.5G.1")
// — every mapping below agrees with it.

// ── Band codes ──────────────────────────────────────────────────────────────
// The token BEFORE the sector letter in an LTE cell name IS the RET band code
// ("…_7A_1" → band 7).
//
// The labels are the crew's own key, supplied by the tech 2026-08-20:
//   7 = 700 LTE   7F = 700 Fnet   8 = 850 LTE   9 = 1900 PCS
//   2 = 2100 AWS  3 = 2300 WCS
// Rows the tech didn't list are filled from the AT&T table using the same
// naming style, so an uncommon band still resolves rather than dropping out.
// A band resolves to a frequency name plus an optional qualifier. They are kept
// SEPARATE because the qualifier can change: where the tech's key says "LTE",
// the same frequency carried on both an LTE and an NR cell renders as
// "LTE & 5G" (their call, 2026-08-20 — "Both 850 are classed as LTE. I'm fine
// with calling them LTE & 5G"). Qualifiers that are band names rather than
// technologies — PCS, AWS, WCS, Fnet — never change.
interface BandLabel {
  freq: string;
  // "LTE" here means "a technology word that reflects the cells present";
  // anything else is a fixed band name. undefined = no qualifier at all.
  qualifier?: string;
}
const BAND_CODE_LABEL: Record<string, BandLabel> = {
  "1": { freq: "mmWave" },
  "2": { freq: "2100", qualifier: "AWS" }, // 2100 MHz (AWS1) only
  "3": { freq: "2300", qualifier: "WCS" }, // 2300 MHz (WCS) only
  "5": { freq: "C-BAND" }, // 3.45 GHz DoD-Band (Band 77)
  // Band 66 is AWS3 in the AT&T table, but the crew's key labels it 2100 AWS —
  // same as band 2 — and the RFDS agrees (its Cell Details USeId reads
  // "…A.AWS.5G.1" for the N066 rows). Following the crew: the two collapse to
  // one token, so an antenna carrying both shows 2100 AWS once.
  "6": { freq: "2100", qualifier: "AWS" },
  "7": { freq: "700", qualifier: "LTE" }, // 700 MHz B & C Band only
  "8": { freq: "850", qualifier: "LTE" }, // 850 MHz only
  "9": { freq: "1900", qualifier: "PCS" }, // 1900 MHz (PCS)
  "141": { freq: "700", qualifier: "Fnet" }, // upper 700 MHz - Band 14 (FirstNet)
  "145": { freq: "700 D&E" }, // 700 MHz D & E Band only
  "167": { freq: "C-BAND" }, // 3.8 GHz C-Band (Band 77)
};

// ── NR bands ────────────────────────────────────────────────────────────────
// 5G cells name their 3GPP NR band zero-padded to three digits ("…_N066A_1" →
// n66). The crew's key maps these onto the SAME labels as their LTE twins
// (N005 = 850, N002 = 1900 PCS, N066 = 2100 AWS, N077 = C-BAND), so a band
// carried on both LTE and NR shows once rather than twice.
const NR_BAND_LABEL: Record<string, BandLabel> = {
  "2": { freq: "1900", qualifier: "PCS" }, // N002 ← 1900 MHz (PCS)
  "4": { freq: "2100", qualifier: "AWS" }, // N004 ← 2100 MHz (AWS1)
  "5": { freq: "850", qualifier: "LTE" }, // N005 ← 850 MHz
  "12": { freq: "700", qualifier: "LTE" }, // N012 ← 700 MHz B & C Band
  "14": { freq: "700", qualifier: "Fnet" }, // N014 ← Band 14 (FirstNet)
  "17": { freq: "700", qualifier: "LTE" }, // N017 ← 700 MHz B & C Band
  "29": { freq: "700 D&E" }, // N029 ← 700 MHz D & E Band
  "30": { freq: "2300", qualifier: "WCS" }, // N030 ← 2300 MHz (WCS)
  "66": { freq: "2100", qualifier: "AWS" }, // N066 ← Band 66 (AWS3) — see "6"
  "77": { freq: "C-BAND" }, // N077 ← Band 77
  "260": { freq: "mmWave" }, // n260
};

// ── Technology letter codes ─────────────────────────────────────────────────
// The RET workbook's Tech column. Exported because the RET output writes the
// letter, while the tape drop dropdown shows the long form.
export const TECH_CODE_LABEL: Record<string, string> = {
  F: "License protection/FCC compliance/FWLL",
  G: "Reserved",
  H: "Reserved",
  J: "LTE-5G NR",
  K: "UMTS-LTE-5G NR",
  L: "LTE",
  M: "Exception",
  N: "None/Reserved",
  P: "Reserved",
  Q: "Reserved",
  R: "5G NR",
  S: "Reserved",
  T: "Reserved",
  U: "UMTS",
  V: "UMTS-LTE",
  Y: "UMTS-5G NR",
};

export interface AntennaPosition {
  // "A-3" / "A-4" — the Sec-Pos row. Kept for ordering and for labelling the
  // dropdown row when a sector carries more than one antenna.
  position?: string;
  // The Model row, verbatim. AT&T model naming is irregular enough
  // ("NNHH-65B-R4" vs "NNH4-65B-R6") that normalising it corrupts real values.
  model: string;
  // The Linked Cells row — every cell homed on this antenna.
  linkedCells: string[];
}

export type AntennaPositionsBySector = Record<string, AntennaPosition[]>;

// An LTE cell: {site}_{bandCode}{sector}_{carrier} with an optional _F
// (FirstNet) suffix — e.g. "WTL01819_7A_2_F", "WTL01819_2A_1".
const LTE_CELL_RE = /_(\d{1,3})([A-D])_(\d+)(_F)?$/i;
// A 5G NR cell: {site}_N{3-digit NR band}{sector}_{carrier} — e.g.
// "WTXN001819_N066A_1". Keyed off the band token, NOT the site prefix: AT&T's
// own examples use "GASN001234" while this RFDS uses "WTXN001819".
const NR_CELL_RE = /_N(\d{3})([A-D])_(\d+)$/i;

interface DecodedCell {
  label: BandLabel | null; // null when the band isn't in either table
  firstNet: boolean;
  nr: boolean;
}

// Decode one linked-cell name into its band label + generation.
export function decodeLinkedCell(cell: string): DecodedCell | null {
  const raw = (cell || "").trim();
  if (!raw || raw === "-") return null;

  const nr = NR_CELL_RE.exec(raw);
  if (nr) {
    // Strip the zero-padding: "066" → "66", "002" → "2".
    const band = String(parseInt(nr[1], 10));
    return {
      label: NR_BAND_LABEL[band] || null,
      firstNet: band === "14",
      nr: true,
    };
  }

  const lte = LTE_CELL_RE.exec(raw);
  if (lte) {
    // "_F" makes it the FirstNet carrier of that band, REPLACING the plain
    // label — the crew's key reads "7 = 700 LTE, 7F = 700 Fnet", two variants
    // of one band, not two carriers. An antenna homing only 7A_2_F therefore
    // shows "700 Fnet" alone rather than "700 LTE/700 Fnet".
    const firstNet = !!lte[4];
    return {
      label: firstNet
        ? BAND_CODE_LABEL["141"]
        : BAND_CODE_LABEL[lte[1]] || null,
      firstNet,
      nr: false,
    };
  }
  return null;
}

// Build the "/"-separated option text for one antenna:
//   NNHH-65B-R4/2100 AWS/700 LTE/850 LTE & 5G
//   NNH4-65B-R6/700 Fnet/1900 PCS
//
// Frequencies keep RFDS order and are de-duplicated — an NR cell shares its LTE
// twin's label, so a band carried on both appears once. Where the qualifier is
// the technology ("LTE"), it reflects what the antenna actually homes: LTE
// cells only → "LTE", NR only → "5G", both → "LTE & 5G". Band-name qualifiers
// (PCS, AWS, WCS, Fnet) are fixed and pass through untouched. There is no
// trailing overall LTE/5G marker — the tech sets that at site setup.
export function describeAntenna(pos: AntennaPosition): string {
  // Per frequency, in first-seen order: its qualifier + which generations the
  // linked cells actually cover.
  const order: string[] = [];
  const byFreq = new Map<
    string,
    { qualifier?: string; lte: boolean; nr: boolean }
  >();

  for (const cell of pos.linkedCells || []) {
    const d = decodeLinkedCell(cell);
    if (!d || !d.label) continue;
    const { freq, qualifier } = d.label;
    // FirstNet is its own line even though it shares the 700 frequency, so key
    // it separately — otherwise "700 LTE" and "700 Fnet" would collapse.
    const key = qualifier === "Fnet" ? `${freq} Fnet` : freq;
    let entry = byFreq.get(key);
    if (!entry) {
      entry = { qualifier, lte: false, nr: false };
      byFreq.set(key, entry);
      order.push(key);
    }
    if (d.nr) entry.nr = true;
    else entry.lte = true;
  }

  const parts: string[] = [];
  for (const key of order) {
    const entry = byFreq.get(key)!;
    const freq = entry.qualifier === "Fnet" ? key.replace(/ Fnet$/, "") : key;
    let qualifier = entry.qualifier;
    // Only the combined case changes the word. An NR-only band keeps the
    // table's qualifier rather than becoming "5G": the tech's key classes both
    // 850 cells as LTE, and ARL01389 has a real antenna homing N005 with no
    // band-8 twin — it should read "850 LTE" there, not "850 5G".
    if (qualifier === "LTE" && entry.lte && entry.nr) {
      qualifier = "LTE & 5G";
    }
    parts.push([freq, qualifier].filter(Boolean).join(" "));
  }
  const model = (pos.model || "").trim();
  return [model, ...parts].filter(Boolean).join("/");
}

// Every option for one sector's Model/Technology dropdowns. The crew picks the
// entry matching the antenna they are taping, so all of the sector's antennas
// are offered on each row. Returns [] when the RFDS hasn't been ingested (or
// carried no antenna rows) — the caller then falls back to the template's own
// static options rather than showing an empty list.
export function antennaOptionsForSector(
  bySector: AntennaPositionsBySector | undefined | null,
  sector: string | null | undefined,
): string[] {
  if (!bySector || !sector) return [];
  const rows = bySector[sector.toUpperCase()];
  if (!Array.isArray(rows)) return [];
  const out: string[] = [];
  for (const row of rows) {
    const text = describeAntenna(row);
    if (text && !out.includes(text)) out.push(text);
  }
  return out;
}
